'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGoogleLogin as useGoogleLoginOauth } from '@react-oauth/google';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useRouter } from '~/i18n/routing';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TAG_TYPES,
  getQueryClient
} from '~/lib/config';
import { routes } from '~/lib/constants';
import useGoogleLogin from '~/lib/hooks/auth/use-google-login';
import useLogin from '~/lib/hooks/auth/use-login';
import useResend from '~/lib/hooks/auth/use-resend-code';
import {
  LOGIN_ERROR_CODE,
  PostLoginResponseSuccess
} from '~/lib/services/api/user';
import { setToken } from '~/lib/util/tokens';
import { sleep } from '~/lib/utils';
import { Button } from '~/modules/common/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader
} from '~/modules/common/components/ui/card';
import { Form } from '~/modules/common/components/ui/form';
import { toast } from '~/modules/common/components/ui/use-toast';

import {
  LoginFormValues,
  getInitialDefaultValues,
  getLoginFormSchema
} from './constants';
import { TypographyH1 } from '../../common/components/typography/typography';

export function LoginForm () {
  const t = useTranslations('page.login');

  const router = useRouter();
  const queryClient = getQueryClient();
  const searchParams = useSearchParams();
  const invitationToken = searchParams.get('invitationToken') || undefined;

  const { mutateAsync: traditionalLogin, isPending: _isPending } = useLogin();
  const { mutateAsync: googleLogin, isPending: isGooglePending } =
    useGoogleLogin();
  const { mutateAsync: resendVerificationCode } = useResend();
  const loginFormSchema = getLoginFormSchema({ t });
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: getInitialDefaultValues({}),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);

  useEffect(() => {
    if (invitationToken) {
      toast({ title: t('inviteToast'), duration: 60 * 60 * 1000 });
    }
  }, [invitationToken, t]);

  const handleLogin = async (
    res:
      | PostLoginResponseSuccess['data']
      | {
          email: string
          userName: string
          accessToken: string
          refreshToken: string
        },
    invitationToken?: string
  ) => {
    try {
      // await queryClient.invalidateQueries();

      await queryClient.setQueryData([TAG_TYPES.AUTH], {
        userId: res.email
      });

      if (invitationToken) {
        const destination = `${routes.projects}/invite/?invitationToken=${invitationToken}`;
        router.push(destination);
      }

      await Promise.all([
        setToken(res.accessToken, ACCESS_TOKEN_KEY),
        setToken(res.refreshToken, REFRESH_TOKEN_KEY)
      ]);

      router.push(routes.dashboard);
    } catch (err) {
      console.log(err, 'error');
      const { error } = err as APIError;
      if (error?.code !== 500) {
        toast({
          title: error.message || t('invalidEmailOrPassword')
        });
      }
    }
  };

  const handleTraditionalLogin = async (data: LoginFormValues) => {
    try {
      const res = await traditionalLogin(data);

      if ('data' in res) {
        await handleLogin(res.data, invitationToken);
      }
    } catch (err) {
      console.log(err);
      const baseError = err as APIError;
      if (baseError && 'error' in baseError) {
        const { error } = baseError;
        if (error.code === LOGIN_ERROR_CODE) {
          queryClient.setQueryData([TAG_TYPES.AUTH], { email: data.email });
          await resendVerificationCode({ email: data.email });
          toast({ title: t('verificationSent') });
          await sleep(1000);
          router.push('/verify');
          return;
        }
        if (error.code === 400) {
          toast({
            title: error.message || t('invalidEmailOrPassword')
          });
        }
      }
    }
  };

  const handleGoogleLogin = useGoogleLoginOauth({
    flow: 'auth-code',
    onSuccess: async (credentialResponse) => {
      console.log(credentialResponse, 'credentialResponse');
      try {
        const res = await googleLogin({
          token: credentialResponse.code
        });
        if ('data' in res) {
          setIsLoadingDashboard(true);
          await new Promise((resolve) => setTimeout(resolve, 5000));
          await handleLogin(res.data, invitationToken);
        }
      } catch (error) {
        setIsLoadingDashboard(false);
        const baseError = error as APIError;
        if (baseError && 'error' in baseError) {
          toast({
            title: baseError.error.message || t('invalidEmailOrPassword')
          });
        }
        if (baseError && 'detail' in baseError) {
          toast({
            title: baseError.detail || t('invalidEmailOrPassword')
          });
        }
      }
    },
    onError: () => {
      setIsLoadingDashboard(false);
      toast({
        title: t('invalidEmailOrPassword')
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleTraditionalLogin)}
        className="max-w-[440px] w-full mx-auto px-4"
      >
        <Card className="mx-auto w-full rounded-2xl shadow-xl border border-gray-100 bg-white/90 backdrop-blur">
          <CardHeader className="text-center space-y-2">
            {/* Título principal */}
            <TypographyH1 className="text-3xl font-bold text-gray-900">
              {t('login')}
              {(process.env.NEXT_PUBLIC_BACKOFFICE_ENV === 'dev' ||
                process.env.NEXT_PUBLIC_BACKOFFICE_ENV === 'stage') && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({process.env.NEXT_PUBLIC_BACKOFFICE_ENV})
                </span>
              )}
            </TypographyH1>
            {/* Subtítulo */}
            <CardDescription className="text-gray-500">
              {t('description')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid gap-6">
              {/* Botón Google */}
              <Button
                type="button"
                variant="outline"
                className="
                  w-full h-12 rounded-xl border-gray-200
                  text-gray-800 font-medium
                  transition hover:border-gray-300 hover:shadow-md
                "
                disabled={isGooglePending || isLoadingDashboard}
                onClick={(e) => {
                  e.preventDefault();
                  handleGoogleLogin();
                }}
              >
                <svg
                  className="mr-3 size-5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {t('continueWithGoogle')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
      {isLoadingDashboard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="size-8 border-4 border-gray-300 border-t-brand-dark rounded-full animate-spin" />
            <p className="text-brand-dark font-medium">Signing in…</p>
          </div>
        </div>
      )}
    </Form>
  );
}
